import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { GroupHeader, GroupItems } from './CreateProcessTemplateDialog'
import { FIELD_TYPES } from '../../../utility/constants'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { listDataService } from '../../../services/listDataService'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import DeleteIcon from '@mui/icons-material/Delete'

export const FILTER_BASIS = {
  TEAM: 'team',
  USER: 'user',
  FIELD: 'field',
}

const DEFAULT = {
  pivot_field_template_id: null,
  filter_field_template_id: null,
  relatedField: null,
  filter_key: null,
  filter_value: null,
}

const PivotFieldFilterSetup = ({ fieldTemplate, importedFieldList }) => {
  const { user } = useAuthContext()
  const [maxFilter, setMaxFilter] = useState(1)
  const [filters, setFilters] = useState([])
  const [processFields, setProcessFields] = useState([])
  const [fieldListMap, setFieldListMap] = useState({})

  useEffect(() => {
    setMaxFilter(fieldTemplate?.import_from_process_template_id?.split(',')?.length || 1)

    const processIds =
      fieldTemplate?.import_from_process_template_id?.split(',')?.map((it) => +it) || []

    setProcessFields(
      importedFieldList?.filter((it) => {
        return processIds.includes(it.process_template_id)
      })
    )
  }, [fieldTemplate, importedFieldList])

  useEffect(() => {
    ;(async () => {
      if (user) {
        const initialFilters = await fieldTemplateService.getPivotFieldFilters(
          user,
          fieldTemplate.id
        )
        setFilters(
          initialFilters.map((filter, i) => ({
            id: new Date().getTime() + i,
            ...filter,
            relatedField: importedFieldList.find((it) => it.id == filter.filter_field_template_id),
          }))
        )
      }
    })()
  }, [fieldTemplate, user])

  useEffect(() => {
    ;(async () => {
      if (user) {
        const listFieldFilters = filters.filter(
          (it) => it?.relatedField?.data_structure == (FIELD_TYPES.LIST || FIELD_TYPES.LIST_MULTI)
        )

        const listItems = await Promise.all(
          listFieldFilters.map((filter) =>
            listDataService.getData(user, filter.relatedField.list_id)
          )
        )

        const fieldListMap = {}
        listFieldFilters.forEach((filter, index) => {
          fieldListMap[filter.filter_field_template_id] = listItems[index].data
        })

        setFieldListMap(fieldListMap)
      }
    })()
  }, [filters])

  const getSelectListItem = (filter) => {
    const item = fieldListMap[filter.filter_field_template_id]?.find(
      (it) => it.id == filter.filter_value
    )
    if (item) {
      return { value: item, label: item?.value }
    } else {
      return null
    }
  }

  const addConstraint = () => {
    // if (filters.length >= maxFilter) {
    //   toast.error('Reached maximum filter can be set.')
    //   return
    // }
    setFilters([
      ...filters,
      {
        ...DEFAULT,
        id: new Date().getTime(),
        pivot_field_template_id: fieldTemplate.id,
      },
    ])
  }

  const saveConstraints = async () => {
    try {
      await fieldTemplateService.setPivotFieldFilters(user, fieldTemplate.id, filters)
      toast.success('Pivot field filter added!')
    } catch (error) {
      toast.error('Something went wrong!')
    }
  }

  const getFieldOptions = (filter) => {
    if (filter.filter_key == FILTER_BASIS.TEAM) {
      return processFields.filter((it) => {
        return it?.list_id == null && it?.dynamic_field_type?.type == 'teams'
      })
    } else if (filter.filter_key == FILTER_BASIS.USER) {
      return processFields.filter((it) => {
        return it?.list_id == null && it?.dynamic_field_type?.type == 'users'
      })
    } else {
      return processFields
    }
  }

  const onDelete = (filterId) => {
    setFilters(filters.filter((it) => it.id != filterId))
  }

  return (
    <Box>
      <Typography variant="p">Apply Pivot Field Restrictions</Typography>
      <br />
      {filters.map((filter) => {
        return (
          <div style={{ display: 'flex', marginTop: 16, gap: 16, alignItems: 'center' }}>
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <Select
                size="small"
                fullWidth
                key={filter.filter_key || 'filter_key'}
                value={filter.filter_key}
                onChange={(e) => {
                  const updatedFilter = { ...filter, filter_key: e.target.value }
                  setFilters(filters.map((it) => (it.id == filter.id ? updatedFilter : it)))
                }}
              >
                <MenuItem value="" disabled>
                  <em>Filter Basis</em>
                </MenuItem>
                {Object.entries(FILTER_BASIS).map(([key, value]) => {
                  return (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <Autocomplete
                id="Select Imported From Field Template "
                options={getFieldOptions(filter) || []}
                size="small"
                getOptionLabel={(item) => item.field_template_name}
                groupBy={(item) => item.groupHeading}
                value={processFields.find((it) => it.id == filter.filter_field_template_id)}
                onChange={(_, v) => {
                  const updatedFilter = {
                    ...filter,
                    filter_field_template_id: v.id,
                    relatedField: v,
                  }
                  setFilters(filters.map((it) => (it.id == filter.id ? updatedFilter : it)))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Imported From Field Template "
                    color="primary"
                  />
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
              />
            </FormControl>
            {filter?.filter_key == FILTER_BASIS.FIELD ? (
              filter?.relatedField?.data_structure == FIELD_TYPES.LIST ? (
                <Autocomplete
                  value={getSelectListItem(filter) || null}
                  id="list-item-selection"
                  options={fieldListMap[filter.filter_field_template_id]?.map((it) => {
                    return { value: it, label: it.value }
                  })}
                  fullWidth
                  renderInput={(params) => <TextField {...params} label="List Item" />}
                  onChange={(_, v) => {
                    const updatedFilter = { ...filter, filter_value: v?.value?.id }
                    setFilters(filters.map((it) => (it.id == filter.id ? updatedFilter : it)))
                  }}
                  size="small"
                />
              ) : (
                <TextField
                  value={filter.filter_value}
                  id="outlined-basic"
                  label="Value (Optional)"
                  variant="outlined"
                  onChange={(e) => {
                    const updatedFilter = { ...filter, filter_value: e.target.value }
                    setFilters(filters.map((it) => (it.id == filter.id ? updatedFilter : it)))
                  }}
                  size="small"
                />
              )
            ) : null}
            <IconButton onClick={() => onDelete(filter.id)}>
              <DeleteIcon size="small" />
            </IconButton>
          </div>
        )
      })}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 16 }}>
        <Button variant="contained" onClick={addConstraint}>
          Add Constraint
        </Button>
        <Button color="primary" variant="contained" onClick={saveConstraints}>
          Save
        </Button>
      </div>
    </Box>
  )
}

export default PivotFieldFilterSetup
